import type { CityConfig } from "../city-template/types";

export const bloomingtonConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Bloomington",
  state: "Indiana",
  stateAbbr: "IN",
  slug: "bloomington-in",
  route: "/cities/bloomington-in",
  title: "Emergency Passport Bloomington, IN — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Bloomington, Indiana — as fast as 24 hours. Fast passport renewal and new passport applications for Bloomington, Ellettsville, Spencer, Bedford, Martinsville, and all Monroe County residents. A+ BBB rated courier service serving Indiana University students and faculty.",
  h1: "Emergency & Expedited Passport Services in Bloomington, IN",
  heroSubtitle: "Bloomington travelers — from IU students heading abroad to Monroe County families planning international vacations to academic faculty traveling for research — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated registered courier with direct access to the Chicago Passport Agency, just an hour north via I-69.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Bloomington%20Indiana%20university%20town%20with%20iconic%20limestone%20buildings%20and%20the%20Sample%20Gates%20at%20Indiana%20University%2C%20vibrant%20tree%20lined%20Kirkwood%20Avenue%20with%20cafes%20and%20shops%2C%20autumn%20foliage%20on%20a%20beautiful%20college%20campus%2C%20rolling%20hills%20of%20southern%20Indiana%2C%20warm%20golden%20afternoon%20sunlight%2C%20professional%20city%20photography%20with%20rich%20warm%20tones%2C%20classic%20Midwestern%20college%20town%20charm%2C%20no%20text%20or%20people%2C%20editorial%20style%20urban%20photography&width=1800&height=700&seq=bloomington-in-hero-001&orientation=landscape",
  heroImageAlt: "Bloomington Indiana with Indiana University Sample Gates and limestone campus architecture",
  geoPosition: "39.1653;-86.5264",
  geoPlacename: "Bloomington, Indiana",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St Suite 380, Chicago IL 60604",
    distance: "Approximately 230 miles, 3.5 hours via I-65",
  },
  nearbyCitiesZips: [
    { name: "Bloomington", zips: "47401, 47402, 47403, 47404, 47405, 47406, 47407, 47408" },
    { name: "Ellettsville", zips: "47429" },
    { name: "Spencer", zips: "47460" },
    { name: "Bedford", zips: "47421" },
    { name: "Martinsville", zips: "46151" },
    { name: "Nashville", zips: "47448" },
    { name: "Bloomfield", zips: "47424" },
    { name: "Linton", zips: "47441" },
    { name: "Mitchell", zips: "47446" },
    { name: "Brown County area", zips: "47448, 47436" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Registered courier to Chicago Passport Agency — about 3.5 hours from Bloomington via I-65" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting for IU and Monroe County" },
    { icon: "ri-building-line", title: "Indiana University Hub", desc: "Serving IU students, faculty, staff, and Monroe County families" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels from any UPS or FedEx near campus or downtown Bloomington" },
  ],
  internalLinks: [
    { name: "Expedited passport in Indianapolis, IN", route: "/cities/indianapolis" },
    { name: "Emergency passport help in Carmel, IN", route: "/cities/carmel" },
    { name: "Urgent passport services in Fort Wayne, IN", route: "/fort-wayne" },
    { name: "Indiana passport services overview", route: "/states/indiana" },
  ],
  fpcOfficeAddress: "230 S Dearborn St Suite 380, Chicago IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Bloomington, Indiana via secure registered courier to the Chicago Passport Agency (230 S Dearborn St Suite 380, Chicago IL 60604). Approximately 230 miles — about 3.5 hours via I-65. We handle the drive so Bloomington residents, IU students, and Monroe County families never need to make the trip to Chicago themselves.",
  fpcOfficeDistance: "Serving Bloomington, Ellettsville, Spencer, Bedford, Martinsville, Nashville, Bloomfield, Linton, Mitchell, and all of Monroe County and the surrounding south-central Indiana region",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Bloomington and Monroe County. From the Indiana University campus and downtown Kirkwood Avenue to Ellettsville, Spencer, and the Brown County line — we serve all of Monroe County and south-central Indiana.",
  nearbyCitiesIntro: "We serve Bloomington and all nearby communities including Ellettsville, Spencer, Bedford, Martinsville, Nashville, Bloomfield, Linton, and Mitchell. We also serve all of Monroe County, Brown County, and the Indiana University community.",
  howItWorksIntro: "Most Bloomington travelers — especially IU students and faculty — complete everything online and ship documents via overnight courier from any UPS or FedEx near campus. Our registered couriers handle the Chicago Passport Agency delivery so you can focus on your travel plans.",
  mistakesIntro: "Bloomington is a vibrant college town with thousands of IU students, faculty, and researchers traveling internationally every year. Here are the most common passport mistakes we see from Monroe County travelers.",
  testimonialsIntro: "Bloomington travelers — from IU students heading abroad to Monroe County families to academic researchers — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Bloomington, IN? Here are the most common ones we hear from IU and Monroe County travelers.",
  statePageRoute: "/states/indiana",
  statePageName: "Indiana",
  dmvWebsite: "https://www.in.gov/bmv",
  dmvLabel: "Indiana BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "No Chicago drive required — ship from Bloomington", "Perfect for IU study abroad, faculty research trips, and family vacations", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Faster than self-service", "Good for planned study abroad and family travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive to Chicago Passport Agency (~3.5 hours each way)", "Limited appointments for non-emergency travelers"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Bloomington travelers with immediate departure needs — as fast as 24 hours. Ideal for IU students with unexpected study abroad deadlines or last-minute international conference travel.",
      bullets: ["24–48 hour turnaround available", "Chicago Passport Agency — courier handles the 3.5-hour drive", "IU student and faculty travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Bloomington adults with a passport issued within the last 15 years. Skip the post office appointment — ideal for IU faculty and Monroe County professionals.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Bloomington address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for IU students, Monroe County residents, and new citizens in the Bloomington area.",
      bullets: ["Acceptance facility scheduling near campus", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Bloomington residents — whether lost on campus, during a move, or while preparing for travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Bloomington and Monroe County families traveling internationally with kids.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Bloomington and Monroe County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Bloomington IN", description: "Urgent passport processing for Bloomington Indiana travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Bloomington IN", description: "Fast passport renewal for Bloomington Indiana residents with a previously-issued passport." },
    { name: "First-Time Passport Bloomington IN", description: "New passport application services for Bloomington Indiana residents and IU students." },
    { name: "Child Passport Bloomington IN", description: "Minor passport services for Bloomington Indiana families with children under 16." },
  ],
  testimonials: [
    {
      name: "Sarah T.",
      location: "Bloomington, IN — IU Campus",
      rating: 5,
      text: "Study abroad program in Spain starting in 6 days — realized my passport was expired at the last minute. Fast Passport Center got me my renewal in 3 days. Shipped from the UPS Store near campus and everything worked out. Lifesaver for IU students!",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Professor James M.",
      location: "Bloomington, IN — Near Bryan Park",
      rating: 5,
      text: "Research conference in Tokyo with 10 days notice. My passport was valid but had less than 3 months remaining. Fast Passport Center expedited a renewal and I had it in 4 days. Outstanding service for academic travelers.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Amanda K.",
      location: "Ellettsville, IN",
      rating: 5,
      text: "Family trip to Ireland and my two kids needed first-time passports. The Bloomington Post Office was booked for weeks. Fast Passport Center walked us through everything and all three passports arrived in 5 days. Incredible service.",
      service: "Family Passport Bundle",
    },
    {
      name: "David R.",
      location: "Bloomington, IN — Downtown Kirkwood",
      rating: 5,
      text: "Lost my passport somewhere between moving apartments near campus. Had a flight to Mexico City in 8 days. Fast Passport Center replaced it in 48 hours. The courier service to Indianapolis made all the difference.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "bloomington-in-1",
      name: "Bloomington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "520 S Walnut St",
      city: "Bloomington",
      state: "IN",
      zip: "47401",
      phone: "(812) 332-4032",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Bloomington, near the Monroe County Courthouse",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Bloomington acceptance facility. Appointments required — book 2–3 weeks ahead during IU semesters. Extremely busy during study abroad season (September–October and February–March).",
      icon: "ri-mail-line",
    },
    {
      id: "bloomington-in-2",
      name: "Monroe County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "301 N College Ave",
      city: "Bloomington",
      state: "IN",
      zip: "47404",
      phone: "(812) 349-2614",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Downtown Bloomington, Monroe County Courthouse",
      website: "https://www.co.monroe.in.us/",
      websiteLabel: "Monroe County",
      notes: "County clerk passport acceptance. Extended morning hours. Good alternative when post office appointments are full during peak IU travel seasons.",
      icon: "ri-government-line",
    },
    {
      id: "bloomington-in-3",
      name: "Martinsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 E Cunningham St",
      city: "Martinsville",
      state: "IN",
      zip: "46151",
      phone: "(765) 342-2424",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 20 miles north of Bloomington, in Martinsville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative acceptance facility north of Bloomington. Often has better appointment availability than Bloomington locations during peak IU travel seasons.",
      icon: "ri-mail-line",
    },
    {
      id: "bloomington-in-4",
      name: "Bedford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1720 H St",
      city: "Bedford",
      state: "IN",
      zip: "47421",
      phone: "(812) 279-2454",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 25 miles south of Bloomington, in Bedford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Southern Monroe County and Lawrence County acceptance facility. Convenient for Bedford and Mitchell area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "bloomington-in-5",
      name: "Spencer Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 N Main St",
      city: "Spencer",
      state: "IN",
      zip: "47460",
      phone: "(812) 829-2778",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 15 miles west of Bloomington, in Spencer",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Western Monroe County and Owen County acceptance facility. Smaller post office — call ahead for appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 230-mile, 3.5-hour drive to Chicago via I-65", fpc: "No — ship from Bloomington, courier handles it" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "IU Study Abroad", agency: "No special support for students", fpc: "Experienced with IU study abroad deadlines" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "IU students often discover passport issues right before study abroad deadlines. Routine processing takes 8–11 weeks. Spring and fall study abroad seasons see massive demand at the Bloomington Post Office — start expedited service at least 6 weeks before travel.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Student photo services near campus sometimes produce photos that fail passport standards. Photos must be exactly 2x2 inches on a white background — no filters, no smiling with teeth showing, no glasses.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "First-time applicants — especially IU students applying for their first passport — often miss signatures or use the wrong form. DS-11 requires in-person execution at an acceptance facility. DS-82 can be mailed for most renewals.",
    },
    {
      icon: "ri-calendar-line",
      title: "Not Accounting for IU Academic Calendar",
      description: "Study abroad deadlines typically fall in March (for fall programs) and October (for spring programs). Bloomington Post Office appointments fill 3–4 weeks in advance during these periods. Plan accordingly.",
    },
    {
      icon: "ri-global-line",
      title: "Not Checking 6-Month Passport Validity",
      description: "Many countries require 6 months of passport validity beyond your stay. IU students heading to Europe or Asia often discover this requirement too late — especially for semester-long programs.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Assuming Chicago Agency Is Close Enough for Walk-Ins",
      description: "The Chicago Passport Agency requires appointments booked through NPIC — no walk-ins. It's 3.5 hours from Bloomington each way. Fast Passport Center's courier service handles the delivery so you don't need to make this trip at all.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in Bloomington or on IU campus." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at the UPS Store near campus or any FedEx in Bloomington." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for IU or Monroe County documentation errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier delivers to the Chicago Passport Agency — no need to make the 3.5-hour drive." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Bloomington address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Bloomington, IN?", a: "Fast Passport Center offers emergency passport processing in Bloomington, Indiana in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (230 S Dearborn St Suite 380, Chicago IL 60604) — no need to make the 3.5-hour drive yourself." },
    { q: "Does Bloomington have its own passport agency?", a: "No — Bloomington does not have a regional passport agency. The nearest is the Chicago Passport Agency, approximately 230 miles away (about a 3.5-hour drive via I-65). Fast Passport Center's courier service means Bloomington residents never need to make the trip to Chicago themselves." },
    { q: "Can IU international students get passport help in Bloomington?", a: "Fast Passport Center specializes in U.S. passport services for U.S. citizens. International students should contact their home country's embassy or consulate for passport services. We can help IU faculty, staff, and U.S. citizen students with American passport needs." },
    { q: "What airport do Bloomington travelers use?", a: "Indianapolis International Airport (IND) is the primary international airport for Bloomington travelers, located about 50 miles north (approximately 1 hour via I-69). IND offers direct flights to Toronto, Cancun, and Paris (seasonal), plus connecting service worldwide through major hubs." },
    { q: "How much does an expedited passport cost in Bloomington, IN?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Bloomington?", a: "Walgreens (multiple locations including 1400 N Walnut St and 250 S College Ave), CVS (2701 E 3rd St), and the UPS Store (114 S Indiana Ave) all offer passport photo services in Bloomington. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Does Fast Passport Center serve IU faculty traveling for research?", a: "Yes. We serve all Indiana University faculty, staff, and researchers in Bloomington. We understand academic travel deadlines — conferences, research collaborations, and field work — and can coordinate expedited services to match your specific travel schedule." },
    { q: "What about Ellettsville, Spencer, or Bedford residents?", a: "Absolutely. Fast Passport Center serves all of Monroe County and surrounding areas including Ellettsville, Spencer, Bedford, Martinsville, Nashville, Bloomfield, Linton, and Mitchell. Our courier service works the same way for everyone throughout south-central Indiana." },
  ],
  photoLocations: [
    { name: "Walgreens — Bloomington (Walnut)", address: "1400 N Walnut St, Bloomington, IN 47404", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
    { name: "CVS — Bloomington (East 3rd)", address: "2701 E 3rd St, Bloomington, IN 47401", hours: "Mon–Sun: 8AM–9PM", website: "https://www.cvs.com/" },
    { name: "UPS Store — Bloomington", address: "114 S Indiana Ave, Bloomington, IN 47408", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walgreens — Bloomington (College Ave)", address: "250 S College Ave, Bloomington, IN 47404", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
  ],
  dmvLocations: [
    { name: "Indiana BMV — Bloomington", address: "1612 S Liberty Dr, Bloomington, IN 47403", hours: "Mon–Fri: 8:30AM–6:00PM", note: "Indiana Bureau of Motor Vehicles for driver's licenses and REAL ID. Online reservations strongly recommended. Convenient south side location near College Mall.", featured: true },
    { name: "Indiana BMV — Bedford", address: "1520 James Ave, Bedford, IN 47421", hours: "Mon–Fri: 8:30AM–6:00PM", note: "Serves Bedford and Lawrence County. Alternative when Bloomington BMV appointments are full. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "IND",
      name: "Indianapolis International Airport",
      distance: "50 miles north of Bloomington via SR 37 and I-69 — approximately 1 hour",
      address: "7800 Col. H. Weir Cook Memorial Dr, Indianapolis, IN 46241",
      note: "Primary airport for Bloomington and Monroe County. Direct flights to Toronto, Cancun, and Paris (seasonal). Connecting service worldwide through major hubs. The main hub for IU international travelers.",
      flightUrl: "https://www.ind.com/",
      flightLabel: "IND Flight Info",
      featured: true,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International Airport",
      distance: "100 miles south of Bloomington via I-65 — approximately 1.5 hours",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative airport for Bloomington travelers. UPS Worldport hub with domestic and limited international connections. Good option when IND flights are limited.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "SDF Flight Info",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { label: "Monroe County: Birth & Death Certificates", url: "https://www.in.gov/health/vital-records/", icon: "ri-file-text-line", description: "Indiana Department of Health Vital Records — order birth and death certificates for Monroe County and all Indiana counties" },
    { label: "Monroe County Clerk: Marriage Licenses", url: "https://www.co.monroe.in.us/", icon: "ri-heart-line", description: "Monroe County Clerk — marriage license records for Bloomington and Monroe County" },
    { label: "Monroe County: Divorce Decrees", url: "https://www.co.monroe.in.us/", icon: "ri-file-list-3-line", description: "Monroe County Clerk of Courts — divorce decree records for Bloomington and Monroe County" },
    { label: "VitalChek Nationwide Service", url: "https://www.vitalchek.com/", icon: "ri-global-line", description: "Order Indiana vital records online through VitalChek for delivery to your Bloomington address" },
  ],
};